/**
 * Phase 2.5.3 — Consistency Check + Lock-to-Cycle panel.
 *
 * Renders below the two-pane review UI on /admin/jd-normalizer.
 *  • "Run consistency check" runs the 5 detectors and persists findings.
 *  • Each finding card lets the reviewer Accept / Edit / Dismiss with audit trail.
 *  • "Lock to Cycle" snapshots all approved canonical JDs into canonical_jd_versions
 *    and flips approval_status → 'locked'. Disabled until pending=0, critical=0, approved>0.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldAlert, ShieldCheck, AlertCircle, CheckCircle2, XCircle,
    Loader2, RefreshCw, Lock, ChevronDown,
} from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

const SEVERITY = {
    critical: { fg: "text-red-300", bg: "bg-red-500/10 border-red-500/30", label: "Critical" },
    warning:  { fg: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30", label: "Warning" },
    info:     { fg: "text-blue-300", bg: "bg-blue-500/10 border-blue-500/30", label: "Info" },
};

const TYPE_LABEL = {
    reporting_line_gap:    "Reporting line gap",
    role_alias_cluster:    "Role alias cluster",
    cadre_anomaly:         "Cadre anomaly",
    task_overlap:          "Task overlap",
    skills_taxonomy_drift: "Skills taxonomy drift",
};

export default function ConsistencyPanel({ enterpriseId }) {
    const { api } = useAuth();
    const [findings, setFindings] = useState([]);
    const [rollup, setRollup] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [statusFilter, setStatusFilter] = useState("open");
    const [lockOpen, setLockOpen] = useState(false);

    const refresh = useCallback(() => {
        if (!enterpriseId) return;
        Promise.all([
            api.get(`/v2/admin/jd-normalizer/findings/${enterpriseId}`, { params: statusFilter !== "all" ? { status: statusFilter } : {} }),
            api.get(`/v2/admin/jd-normalizer/lock-readiness/${enterpriseId}`),
        ]).then(([f, r]) => {
            setFindings(f.data?.findings || []);
            setReadiness(r.data || null);
        }).catch(() => {});
    }, [api, enterpriseId, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    const runCheck = async () => {
        setRunning(true);
        try {
            const r = await api.post(`/v2/admin/jd-normalizer/check-consistency/${enterpriseId}`);
            setRollup(r.data);
            await refresh();
        } finally { setRunning(false); }
    };

    if (!enterpriseId) return null;

    const criticalCount = (readiness?.blockers || []).find(b => b.reason === "open_critical_findings")?.count || 0;
    const pendingCount = (readiness?.blockers || []).find(b => b.reason === "pending_review_jds_exist")?.count || 0;
    const approvedCount = readiness?.by_status?.approved || 0;
    const lockedCount = readiness?.by_status?.locked || 0;

    return (
        <div className="border border-border/40 bg-card p-5 space-y-5" data-testid="consistency-panel">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-display text-white">Final Consistency Check</h2>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-body">
                        Detects reporting-line gaps, alias clusters, cadre anomalies, task overlap, and skills drift across approved JDs. Critical findings must be resolved before locking to a cycle.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={runCheck} disabled={running}
                        data-testid="run-consistency-btn"
                        className="px-3 py-2 text-xs border border-white/20 text-white hover:bg-white/[0.04] transition-colors disabled:opacity-40 inline-flex items-center gap-2">
                        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Run check
                    </button>
                    <button onClick={() => setLockOpen(true)}
                        disabled={!readiness?.ready_to_lock || busy || lockedCount > 0}
                        data-testid="lock-cycle-btn"
                        className="px-3 py-2 text-xs bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 transition-colors disabled:opacity-40 inline-flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5" /> Lock to cycle
                    </button>
                </div>
            </div>

            {/* Rollup strip */}
            <div className="grid grid-cols-5 gap-3">
                <Stat label="Approved JDs" value={approvedCount} testid="stat-approved" />
                <Stat label="Pending" value={pendingCount} tone={pendingCount ? "warn" : "default"} testid="stat-pending" />
                <Stat label="Locked" value={lockedCount} tone={lockedCount ? "info" : "default"} testid="stat-locked" />
                <Stat label="Open critical" value={criticalCount} tone={criticalCount ? "error" : "success"} testid="stat-critical" />
                <Stat label="All findings" value={findings.length} testid="stat-findings" />
            </div>

            {rollup && (
                <div className="text-[11px] font-mono text-gray-500 px-3 py-2 border border-border/40 bg-background/30">
                    Last run: evaluated {rollup.total_jds_evaluated} JDs → {rollup.total_findings} findings
                    ({Object.entries(rollup.by_severity || {}).map(([k, v]) => `${k}=${v}`).join(" ") || "none"})
                </div>
            )}

            {/* Filter */}
            <div className="flex items-center gap-2 text-xs">
                {["open", "accepted", "edited", "dismissed", "all"].map(s => (
                    <button key={s}
                        onClick={() => setStatusFilter(s)}
                        data-testid={`filter-${s}`}
                        className={cls(
                            "px-2.5 py-1 border font-mono uppercase",
                            statusFilter === s ? "border-white text-white" : "border-border/40 text-gray-500 hover:text-white"
                        )}>
                        {s}
                    </button>
                ))}
            </div>

            {/* Findings */}
            <div className="space-y-3">
                {findings.length === 0 ? (
                    <div className="p-6 text-center border border-dashed border-border/40">
                        <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">No {statusFilter !== "all" ? statusFilter : ""} findings.</p>
                        <p className="text-[10px] text-gray-600 mt-1 font-mono">Run a consistency check to refresh.</p>
                    </div>
                ) : findings.map(f => (
                    <FindingCard key={f.id} finding={f} onResolved={refresh} />
                ))}
            </div>

            <AnimatePresence>
                {lockOpen && (
                    <LockToCycleModal
                        enterpriseId={enterpriseId}
                        cycles={readiness?.cycles || []}
                        onClose={() => { setLockOpen(false); refresh(); }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function Stat({ label, value, tone = "default", testid }) {
    const tones = {
        default: "border-border/40",
        success: "border-emerald-500/40",
        warn: "border-amber-500/40",
        error: "border-red-500/40",
        info: "border-blue-500/40",
    };
    return (
        <div className={cls("p-3 border bg-background/30", tones[tone])} data-testid={testid}>
            <p className="text-[10px] font-mono uppercase text-gray-500">{label}</p>
            <p className="text-2xl font-display font-bold text-white mt-1">{value}</p>
        </div>
    );
}

function FindingCard({ finding, onResolved }) {
    const { api } = useAuth();
    const sev = SEVERITY[finding.severity] || SEVERITY.info;
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [rationale, setRationale] = useState("");
    const isOpen = finding.status === "open";

    const resolve = async (action) => {
        if (action === "dismiss" && !rationale.trim()) return;
        setBusy(true);
        try {
            await api.post(`/v2/admin/jd-normalizer/findings/${finding.id}/resolve`, {
                action,
                resolution_value: action === "accept" ? finding.suggested_resolution : null,
                dismissal_rationale: action === "dismiss" ? rationale : null,
            });
            onResolved?.();
        } finally { setBusy(false); }
    };

    return (
        <div className={cls("border", sev.bg)} data-testid={`finding-${finding.id}`}>
            <button className="w-full flex items-center justify-between p-3 text-left" onClick={() => setOpen(o => !o)}>
                <div className="flex items-center gap-3 min-w-0">
                    <span className={cls("text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border", sev.fg, sev.bg)}>
                        {sev.label}
                    </span>
                    <span className="text-[10px] font-mono uppercase text-gray-500">
                        {TYPE_LABEL[finding.check_type] || finding.check_type}
                    </span>
                    <span className="text-sm text-white truncate">{finding.description}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-mono text-gray-500 uppercase">{finding.status}</span>
                    <ChevronDown className={cls("w-4 h-4 text-gray-500 transition-transform", open && "rotate-180")} />
                </div>
            </button>
            {open && (
                <div className="border-t border-border/30 p-3 space-y-3">
                    <div className="text-[11px] font-mono text-gray-400">
                        Affects {finding.affected_jd_ids.length} JDs
                    </div>
                    {finding.suggested_resolution && (
                        <div className="text-xs">
                            <p className="font-mono text-gray-500 uppercase mb-1">Suggested resolution</p>
                            <pre className="bg-background/50 border border-border/40 p-2 text-[11px] font-mono text-gray-300 overflow-x-auto">
                                {JSON.stringify(finding.suggested_resolution, null, 2)}
                            </pre>
                        </div>
                    )}
                    {finding.dismissal_rationale && (
                        <p className="text-xs text-amber-200">Dismissed: {finding.dismissal_rationale}</p>
                    )}
                    {isOpen && (
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                            <button onClick={() => resolve("accept")} disabled={busy}
                                data-testid={`accept-${finding.id}`}
                                className="px-3 py-1.5 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40">
                                Accept suggestion
                            </button>
                            <input value={rationale} onChange={e => setRationale(e.target.value)}
                                placeholder="Dismissal rationale (required)"
                                data-testid={`rationale-${finding.id}`}
                                className="flex-1 min-w-[200px] bg-background border border-border/40 text-xs text-white px-2 py-1.5 focus:outline-none focus:border-white/30" />
                            <button onClick={() => resolve("dismiss")} disabled={busy || !rationale.trim()}
                                data-testid={`dismiss-${finding.id}`}
                                className="px-3 py-1.5 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40">
                                Dismiss
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function LockToCycleModal({ enterpriseId, cycles, onClose }) {
    const { api } = useAuth();
    const [cycleId, setCycleId] = useState(cycles[0]?.id || "");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const submit = async () => {
        if (!cycleId) return;
        setBusy(true); setError("");
        try {
            const r = await api.post(`/v2/admin/jd-normalizer/lock-to-cycle/${enterpriseId}`, { cycle_id: cycleId });
            setResult(r.data);
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(typeof detail === "string" ? detail : detail?.message || e.message);
        } finally { setBusy(false); }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 max-w-md w-full p-5" data-testid="lock-cycle-modal">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-display text-white inline-flex items-center gap-2">
                        <Lock className="w-4 h-4" /> Lock JDs to cycle
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">×</button>
                </div>
                {result ? (
                    <div className="space-y-2" data-testid="lock-cycle-result">
                        <p className="text-sm text-emerald-300 inline-flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" /> {result.locked_count} canonical JDs locked.
                        </p>
                        <p className="text-[11px] font-mono text-gray-500">cycle_id={result.cycle_id}</p>
                        <div className="flex justify-end pt-3">
                            <button onClick={onClose} className="px-3 py-1.5 text-xs border border-white/20 text-white">Done</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="text-xs text-gray-500 mb-3">
                            All approved canonical JDs will be snapshotted to <span className="font-mono">canonical_jd_versions</span> and marked locked. This is irreversible.
                        </p>
                        <label className="text-[10px] font-mono uppercase text-gray-500">Target cycle</label>
                        <select value={cycleId} onChange={e => setCycleId(e.target.value)}
                            data-testid="cycle-select"
                            className="w-full mt-1 bg-background border border-border/40 text-sm text-white px-2 py-2 focus:outline-none focus:border-white/30">
                            <option value="">— select cycle —</option>
                            {cycles.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                            ))}
                        </select>
                        {error && <p className="text-xs text-red-300 mt-3">{error}</p>}
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white">Cancel</button>
                            <button onClick={submit} disabled={!cycleId || busy}
                                data-testid="confirm-lock-btn"
                                className="px-3 py-1.5 text-xs bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 disabled:opacity-40 inline-flex items-center gap-2">
                                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                                Confirm lock
                            </button>
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
}
